import { useState, useEffect } from 'react';
import { Plus, Trash, ArrowUp, ArrowDown, FloppyDisk, Eye, EyeSlash } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../lib/auth';
import { getConfigTypes, updateConfigOptions } from '../lib/api';
import { toast } from 'sonner';

const CONFIG_LABELS = {
  customer_types: 'Customer Types',
  channels: 'Sales Channels',
  payment_terms: 'Payment Terms',
  credit_statuses: 'Credit Statuses',
  currencies: 'Currencies',
  contact_roles: 'Contact Roles',
  customer_sources: 'Customer Sources',
  lifecycle_stages: 'Lifecycle Stages',
  billing_entities: 'Billing Entities',
  tax_id_types: 'Tax ID Types',
  customer_tags: 'Customer Tags',
};

export default function ConfigManager() {
  const { can } = useAuth();
  const [configs, setConfigs] = useState([]);
  const [selected, setSelected] = useState('');
  const [values, setValues] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newLabel, setNewLabel] = useState('');

  useEffect(() => {
    getConfigTypes().then(res => {
      setConfigs(res.data.configs);
      if (res.data.configs.length > 0 && !selected) {
        setSelected(res.data.configs[0].config_type);
        setValues(res.data.configs[0].values);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const cfg = configs.find(c => c.config_type === selected);
    if (cfg) setValues([...cfg.values]);
  }, [selected, configs]);

  const handleSave = async () => {
    if (!can('manage_config')) { toast.error('Permission denied'); return; }
    setSaving(true);
    try {
      await updateConfigOptions(selected, values);
      setConfigs(prev => prev.map(c => c.config_type === selected ? { ...c, values } : c));
      toast.success('Config saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error saving');
    }
    setSaving(false);
  };

  const addValue = () => {
    if (!newLabel.trim()) return;
    const key = newLabel.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    const maxOrder = Math.max(0, ...values.map(v => v.sort_order || 0));
    setValues([...values, { key, label: newLabel.trim(), sort_order: maxOrder + 1, is_active: true }]);
    setNewLabel('');
  };

  const removeValue = (idx) => setValues(values.filter((_, i) => i !== idx));
  const moveUp = (idx) => {
    if (idx === 0) return;
    const v = [...values];
    [v[idx - 1], v[idx]] = [v[idx], v[idx - 1]];
    v.forEach((item, i) => item.sort_order = i + 1);
    setValues(v);
  };
  const moveDown = (idx) => {
    if (idx === values.length - 1) return;
    const v = [...values];
    [v[idx], v[idx + 1]] = [v[idx + 1], v[idx]];
    v.forEach((item, i) => item.sort_order = i + 1);
    setValues(v);
  };
  const toggleActive = (idx) => {
    const v = [...values];
    v[idx] = { ...v[idx], is_active: !v[idx].is_active };
    setValues(v);
  };
  const editLabel = (idx, label) => {
    const v = [...values];
    v[idx] = { ...v[idx], label };
    setValues(v);
  };

  const canManage = can('manage_config');

  return (
    <div className="space-y-6" data-testid="config-manager">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Config Manager</h1>
          <p className="text-sm text-gray-500 mt-1">Manage system-wide editable lists used across all modules</p>
        </div>
        {canManage && (
          <Button onClick={handleSave} disabled={saving} data-testid="save-config-btn">
            <FloppyDisk size={18} className="mr-2" />{saving ? 'Saving...' : 'Save Changes'}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="space-y-1">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Config Types</div>
          {configs.map(c => (
            <button
              key={c.config_type}
              onClick={() => setSelected(c.config_type)}
              className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                selected === c.config_type ? 'bg-gray-900 text-white' : 'hover:bg-gray-100 text-gray-700'
              }`}
              data-testid={`config-type-${c.config_type}`}
            >
              <div className="flex justify-between items-center">
                <span>{CONFIG_LABELS[c.config_type] || c.config_type}</span>
                <Badge variant="secondary" className="text-xs">{c.values.length}</Badge>
              </div>
            </button>
          ))}
        </div>

        <div className="md:col-span-3">
          {selected && (
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between mb-2">
                <h2 className="font-semibold text-gray-900">{CONFIG_LABELS[selected] || selected}</h2>
                <span className="text-sm text-gray-500">{values.length} values ({values.filter(v => v.is_active).length} active)</span>
              </div>

              <div className="space-y-1">
                {values.map((v, idx) => (
                  <div key={v.key + idx} className={`flex items-center gap-2 px-3 py-2 rounded-md border ${!v.is_active ? 'bg-gray-50 opacity-60' : 'bg-white'}`}
                    data-testid={`config-value-${idx}`}>
                    <span className="text-xs text-gray-400 w-6">{idx + 1}.</span>
                    {canManage ? (
                      <Input value={v.label} onChange={e => editLabel(idx, e.target.value)} className="flex-1 h-8 text-sm" />
                    ) : (
                      <span className="flex-1 text-sm">{v.label}</span>
                    )}
                    {!v.is_active && <Badge variant="secondary" className="text-xs">Inactive</Badge>}
                    {canManage && (
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => moveUp(idx)}><ArrowUp size={12} /></Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => moveDown(idx)}><ArrowDown size={12} /></Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => toggleActive(idx)}>
                          {v.is_active ? <EyeSlash size={12} /> : <Eye size={12} />}
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => removeValue(idx)}><Trash size={12} /></Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {canManage && (
                <div className="flex gap-2 mt-3 pt-3 border-t">
                  <Input placeholder="New value..." value={newLabel} onChange={e => setNewLabel(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addValue(); }}}
                    className="flex-1" data-testid="new-config-value" />
                  <Button variant="outline" onClick={addValue} data-testid="add-config-value">
                    <Plus size={16} className="mr-1" />Add
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
